/**
 * AccountDashboard — Feb 2026 · account panel rebuilt as a commute dashboard.
 *
 * Order (from the spec, non-negotiable):
 *   1. Your plan          — days, pickup/return windows, current rate.
 *                            Compact prompt when nothing booked.
 *   2. Savings counter    — "$X saved vs rideshare since <month>".
 *                            Hidden below $50 lifetime.
 *   3. Rate status        — permanent nudge ("add 1 day → 15%").
 *   4. Next charge        — subscribers only.
 *   5. Payment status     — one line unless there's a problem.
 *   6. Refer a route-mate — sector-framed copy.
 *   7. Settings           — one row, expands into a nested slide-in
 *                            with everything from Preferences / Support /
 *                            Legal / Quick Actions (minus dupes).
 *
 * Surface: glass — rgba(255,255,255,.05) + backdrop-filter blur(30) saturate(1.25).
 * No card borders (except selected rows). Montserrat for figures + labels,
 * Inter for body copy. Right-aligned figures where present.
 *
 * Data comes from GET /api/me/commute-summary in a single round-trip.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronRight, CalendarDays, CreditCard, MapPin, Bell, User,
  Sun, Accessibility, HelpCircle, Mail, AlertTriangle, FileCheck,
  FileText, Eye, Shield, Share2, ArrowLeft,
} from "lucide-react";
import { api } from "@/shared/lib/api";
import { formatTime12, formatTimeWindow12 } from "@/shared/lib/format";

/* ---------- glass helpers ----------
 * Feb 2026 · dusk-glass rider chrome. Cards render as translucent
 * light-on-dark glass panels sitting on the drawer's darker glass
 * surface. Tokens live in index.css so we never hand-pick per file.
 */
const GLASS_SURFACE = {
  background: "var(--dusk-glass)",
  backdropFilter: "blur(20px) saturate(1.1)",
  WebkitBackdropFilter: "blur(20px) saturate(1.1)",
  boxShadow: "var(--dusk-glass-border)",
};

function DashboardCard({ children, testId, tone = "glass", onClick, dim }) {
  const style = {
    ...(tone === "glass" ? GLASS_SURFACE : {}),
    borderRadius: 20,
    padding: "14px 16px",
    color: "var(--dusk-text)",
    opacity: dim ? 0.55 : 1,
    boxShadow: tone === "attention"
      ? "inset 0 0 0 1.5px rgba(245,166,35,0.85)"
      : (GLASS_SURFACE.boxShadow),
  };
  if (onClick) {
    return (
      <button type="button" onClick={onClick} style={style} className="w-full text-left" data-testid={testId}>
        {children}
      </button>
    );
  }
  return <div style={style} data-testid={testId}>{children}</div>;
}

function Eyebrow({ children, tone = "orange" }) {
  return (
    <div
      style={{
        fontFamily: "var(--font-display)",
        fontWeight: 800,
        fontSize: 10,
        letterSpacing: "2.2px",
        color: tone === "amber" ? "#C97A00" : "#ed6500",
        textTransform: "uppercase",
        marginBottom: 4,
      }}
    >{children}</div>
  );
}
function Body({ children, size = 15.5, weight = 700, right }) {
  return (
    <div
      style={{
        fontFamily: "var(--font-display)",
        fontWeight: weight,
        fontSize: size,
        color: "var(--dusk-text)",
        letterSpacing: "-0.2px",
        lineHeight: 1.25,
        textAlign: right ? "right" : "left",
        fontVariantNumeric: "tabular-nums",
      }}
    >{children}</div>
  );
}
function Muted({ children, size = 12.5 }) {
  return (
    <div
      style={{
        fontFamily: "var(--font-body)",
        fontWeight: 400,
        fontSize: size,
        color: "var(--dusk-text-muted)",
        letterSpacing: "0.01em",
        lineHeight: 1.45,
      }}
    >{children}</div>
  );
}

/* ---------- individual dashboard sections ---------- */
function PlanCard({ plan }) {
  if (!plan || !plan.has_plan) {
    return (
      <DashboardCard testId="dashboard-plan-empty">
        <Eyebrow>YOUR PLAN</Eyebrow>
        <Body size={15}>No commute booked</Body>
        <div className="mt-1"><Muted>Tap the pill above to book your commute.</Muted></div>
      </DashboardCard>
    );
  }
  const days = (plan.days || []).join(", ");
  const rideLabel = plan.ride_type === "private" ? "Private" : `${plan.seats} rider${plan.seats === 1 ? "" : "s"} · Shared`;
  // 12-hour everywhere for rider-facing surfaces. Prefer the pickup
  // window (e.g., "07:00-07:15" → "7:00–7:15 AM") when the backend
  // supplies it; otherwise render the raw pickup/return times.
  const pickupLabel = plan.pickup_window
    ? formatTimeWindow12(plan.pickup_window)
    : formatTime12(plan.pickup_time);
  const returnLabel = plan.return_window
    ? formatTimeWindow12(plan.return_window)
    : formatTime12(plan.return_time);
  return (
    <DashboardCard testId="dashboard-plan">
      <Eyebrow>YOUR PLAN</Eyebrow>
      <div className="flex items-baseline justify-between">
        <Body>{days || "—"}</Body>
        <Body right>${Number(plan.daily_rate).toFixed(2)}/day</Body>
      </div>
      <div className="mt-1 flex items-baseline justify-between">
        <Muted>Pickup {pickupLabel} · Return {returnLabel}</Muted>
        <Muted>{rideLabel}</Muted>
      </div>
    </DashboardCard>
  );
}

function SavingsCard({ savings }) {
  if (!savings || !savings.show) return null;
  const month = (() => {
    if (!savings.since_month_iso) return "your first ride";
    try {
      const d = new Date(savings.since_month_iso);
      return d.toLocaleDateString(undefined, { month: "long" });
    } catch (_) { return ""; }
  })();
  return (
    <DashboardCard testId="dashboard-savings">
      <Eyebrow>SAVED VS RIDESHARE</Eyebrow>
      <Body size={26} weight={900}>${savings.amount_usd}</Body>
      <div className="mt-1"><Muted>since {month} · {savings.formula_note}</Muted></div>
    </DashboardCard>
  );
}

function RateStatusCard({ rate_status }) {
  if (!rate_status) return null;
  return (
    <DashboardCard testId="dashboard-rate-status">
      <Eyebrow>RATE STATUS</Eyebrow>
      <Body>{rate_status.copy}</Body>
      {rate_status.at_best_band && (
        <div className="mt-1"><Muted>Best rate — full-week commitment.</Muted></div>
      )}
    </DashboardCard>
  );
}

function NextChargeCard({ next_charge }) {
  if (!next_charge) return null;
  return (
    <DashboardCard testId="dashboard-next-charge">
      <Eyebrow>NEXT CHARGE</Eyebrow>
      <div className="flex items-baseline justify-between">
        <Body>{next_charge.day_label}</Body>
        <Body right>${Number(next_charge.amount_usd).toFixed(2)}</Body>
      </div>
    </DashboardCard>
  );
}

function PaymentStatusRow({ payment_status, onNavigate }) {
  if (!payment_status) return null;
  const attention = payment_status.attention;
  return (
    <DashboardCard testId="dashboard-payment-status" tone={attention ? "attention" : "glass"} onClick={attention ? (() => onNavigate("/payment-methods")) : undefined}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <CreditCard size={16} color={attention ? "#F5A623" : "rgba(255,255,255,0.55)"} strokeWidth={2} />
          <Body size={14} weight={600}>{payment_status.message}</Body>
        </div>
        {attention && <ChevronRight size={14} color="#F5A623" />}
      </div>
    </DashboardCard>
  );
}

function ReferralCard({ onNavigate }) {
  return (
    <DashboardCard testId="dashboard-referral" onClick={() => onNavigate("/referrals")}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <Share2 size={16} color="#ed6500" strokeWidth={2} />
          <div>
            <Body size={14} weight={700}>Refer someone on your route</Body>
            <div className="mt-0.5"><Muted>Same pickup sector · same route — worth 3× a random referral.</Muted></div>
          </div>
        </div>
        <ChevronRight size={14} color="rgba(255,255,255,0.55)" />
      </div>
    </DashboardCard>
  );
}

function SettingsRow({ onOpen }) {
  return (
    <DashboardCard testId="dashboard-settings-row" onClick={onOpen}>
      <div className="flex items-center justify-between gap-3">
        <Body size={14} weight={700}>Settings</Body>
        <ChevronRight size={14} color="rgba(255,255,255,0.55)" />
      </div>
    </DashboardCard>
  );
}

/* ---------- Nested Settings slide-in ---------- */
function SettingsPanel({ open, onBack, isAdmin }) {
  const rows = [
    { to: "/trips",           icon: CalendarDays, label: "Trips" },
    { to: "/payment-methods", icon: CreditCard,   label: "Payment methods" },
    { to: "/saved-places",    icon: MapPin,       label: "Saved places" },
    { to: "/preferences/notifications", icon: Bell, label: "Notifications" },
    { to: "/profile",         icon: User,         label: "Profile" },
    { to: "/preferences/accessibility", icon: Accessibility, label: "Accessibility" },
    { to: "/preferences/appearance", icon: Sun, label: "Appearance" },
    { to: "/help",            icon: HelpCircle,   label: "Help center" },
    { to: "/support/contact", icon: Mail,         label: "Contact support" },
    { to: "/support/report",  icon: AlertTriangle,label: "Report issue" },
    { to: "/legal/privacy",   icon: FileCheck,    label: "Privacy policy" },
    { to: "/legal/terms",     icon: FileText,     label: "Terms of service" },
    { to: "/legal/accessibility", icon: Eye,      label: "Accessibility statement" },
  ];
  if (isAdmin) rows.push({ to: "/admin/home", icon: Shield, label: "Admin" });
  return (
    <div
      data-testid="dashboard-settings-panel"
      aria-hidden={!open}
      style={{
        position: "absolute",
        inset: 0,
        transform: open ? "translateX(0)" : "translateX(100%)",
        transition: "transform 380ms cubic-bezier(0.32, 0.72, 0, 1)",
        overflowY: "auto",
        padding: "16px",
        pointerEvents: open ? "auto" : "none",
      }}
    >
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 mb-3 px-1 py-2"
        data-testid="dashboard-settings-back"
      >
        <ArrowLeft size={16} color="rgba(255,255,255,0.55)" />
        <Body size={13} weight={700}>Settings</Body>
      </button>
      <div className="space-y-2">
        {rows.map((r) => (
          <Link
            key={r.to}
            to={r.to}
            className="flex items-center gap-3 py-3 px-3 rounded-2xl"
            style={GLASS_SURFACE}
            data-testid={`dashboard-settings-${r.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <r.icon size={16} color="rgba(255,255,255,0.55)" strokeWidth={1.9} />
            <Body size={14} weight={600}>{r.label}</Body>
            <ChevronRight size={14} color="rgba(255,255,255,0.55)" className="ml-auto" />
          </Link>
        ))}
      </div>
    </div>
  );
}

/* ---------- Root ---------- */
export default function AccountDashboard() {
  const [summary, setSummary] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/me/commute-summary");
        if (!cancelled) setSummary(data);
      } catch (_) {
        if (!cancelled) setSummary(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const plan = summary?.plan;
  const savings = summary?.savings;
  const rateStatus = summary?.rate_status;
  const nextCharge = summary?.next_charge;
  const paymentStatus = summary?.payment_status;
  const isAdmin = !!summary?.is_admin;

  return (
    <div className="relative min-h-full" data-testid="account-dashboard">
      <div className="space-y-3 p-4" style={{ transition: "opacity 220ms", opacity: settingsOpen ? 0 : 1 }}>
        <PlanCard plan={plan} />
        <SavingsCard savings={savings} />
        <RateStatusCard rate_status={rateStatus} />
        <NextChargeCard next_charge={nextCharge} />
        <PaymentStatusRow payment_status={paymentStatus} onNavigate={nav} />
        <ReferralCard onNavigate={nav} />
        <SettingsRow onOpen={() => setSettingsOpen(true)} />
      </div>
      <SettingsPanel open={settingsOpen} onBack={() => setSettingsOpen(false)} isAdmin={isAdmin} />
    </div>
  );
}
